import type { ConnectorProject } from '@saaga/shared/interfaces/connector.interface'
import { BarChart3Icon, SearchIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

export interface ConnectorProjectRowProps {
  project: ConnectorProject
  onToggleActive: (projectId: string, isActive: boolean) => void
  className?: string
}

const SERVICE_TYPE_ICON_MAP: Record<string, typeof SearchIcon> = {
  search_console: SearchIcon,
  analytics: BarChart3Icon,
}

/**
 * Displays a single connector project with a toggle to activate/deactivate it.
 * Active projects make their tools available to LLM agents in chat.
 */
export const ConnectorProjectRow = ({
  project,
  onToggleActive,
  className,
}: ConnectorProjectRowProps): ReactNode => {
  const Icon = SERVICE_TYPE_ICON_MAP[project.serviceType] ?? SearchIcon

  return (
    <div
      className={cn(
        'connector-project-row flex items-center justify-between rounded-md border px-4 py-3',
        className
      )}
    >
      <div className="flex items-center gap-3">
        <Icon className="size-4 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">{project.displayName}</p>
          {project.externalId !== project.displayName && (
            <p className="text-muted-foreground text-xs">{project.externalId}</p>
          )}
        </div>
      </div>
      <Switch
        className="connector-project-toggle"
        checked={project.isActive}
        onCheckedChange={(checked) => onToggleActive(project.id, checked)}
        aria-label={`Toggle ${project.displayName}`}
      />
    </div>
  )
}

ConnectorProjectRow.displayName = 'ConnectorProjectRow'
